<template>
  <div
    class="slider"
    :class="{
      future: indexSlider > maxAnlsisIndex
    }"
  >
    <div class="controller">
      <button
        data-button="prev"
        :disabled="disablePrev"
        @click="prevSlider"
      />
      <p>
        {{ `${now.add(indexSlider, 'hour').locale(ja).format('D(ddd) H')}時` }}
        <span>&nbsp;予想</span>
      </p>
      <button
        data-button="next"
        :disabled="disableNext"
        @click="nextSlider"
      />
    </div>
    <div class="content">
      <button
        :class="{
          play: !playing,
          pause: playing
        }"
        @click="playSlider"
      />
      <div class="range">
        <input
          v-model.number="indexSlider"
          type="range"
          name="time"
          :min="min"
          :max="max"
          :step="1"
          @click="stop"
        >
        <div class="list">
          <template
            v-for="item in array"
            :key="item.text"
          >
            <p
              class="scale"
              :data-status="item.isFuture && 'future'"
              :style="{ width: 100 / array.length + '%' }"
            >
              {{ item.text }}
            </p>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import ja from 'dayjs/locale/ja'

dayjs.extend(utc)
dayjs.extend(timezone)

const playing = ref(false)
let clearId = null

const props = defineProps({
  maxAnlsisIndex: {
    type: Number,
    required: true
  },
  min: {
    type: Number,
    required: true
  },
  max: {
    type: Number,
    required: true
  },
  array: {
    type: Array,
    required: true
  },
  now: {
    type: Object,
    required: true
  }
})

const indexSlider = defineModel({ type: Number, required: true })

const disablePrev = computed(() => {
  return indexSlider.value <= props.min
})
const disableNext = computed(() => {
  return indexSlider.value >= props.max
})

const prevSlider = () => {
  stop()
  indexSlider.value = Math.max(indexSlider.value - 1, props.min)
}
const nextSlider = () => {
  stop()
  indexSlider.value = Math.min(indexSlider.value + 1, props.max)
}
const playSlider = () => {
  if (playing.value) {
    stop()
  } else {
    play()
  }
}
const anim = () => {
  if (indexSlider.value < props.max) {
    indexSlider.value += 1
  } else {
    indexSlider.value = props.min
  }

  if (indexSlider.value >= props.max - 1) {
    clearId = setTimeout(anim, 1000)
  } else {
    clearId = setTimeout(anim, 500)
  }
}
const stop = () => {
  playing.value = false
  clearTimeout(clearId)
}
const play = () => {
  playing.value = true
  clearTimeout(clearId)
  anim()
}
</script>

<style lang="scss" scoped>
.slider {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
  .controller {
    display: flex;
    align-items: center;
    gap: 5px;
    p {
      min-width: 150px;
      padding: 6px 15px 5px;
      background-color: #fff;
      border-radius: 100vmax;
      color: #303030;
      font-size: 14px;
      line-height: 14px;
      text-align: center;
      span {
        display: none;
      }
      &:is(.future *) {
        color: #DD7308;
        // font-weight: 600;
        span {
          display: inline-block;
        }
      }
    }
  }
  .content {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
  }
  button {
    width: 25px;
    height: 25px;
    border-radius: 4px;
    background-color: #fff;
    box-shadow: 0 2px 2px 0 rgb(0 0 0 / 0.15);
    background-position: center;
    background-repeat: no-repeat;
    background-size: 12px;
    cursor: pointer;
    &[data-button="prev"] {
      background-image: url("~/assets/images/common/icon_prev.svg");
    }
    &[data-button="next"] {
      background-image: url("~/assets/images/common/icon_next.svg");
    }
    &.play {
      background-image: url("~/assets/images/common/icon_play.svg");
    }
    &.pause{
      background-image: url("~/assets/images/common/icon_pause.svg");
    }
    @media (any-hover: hover) {
      transition: opacity .2s ease-in-out;
      &:hover {
        opacity: .6;
      }
    }
  }
  button:disabled {
    pointer-events: none;
    filter: contrast(.5);
  }
  .range {
    display: flex;
    flex-direction: column;
    gap: 5px;
    flex: 1;
    input {
      position: relative;
      -webkit-appearance: none;
      appearance: none;
      cursor: pointer;
      outline: none;
      height: 3px;
      margin-inline: auto;
      // background-color: #8793ae;
      background: linear-gradient(90deg,#fff,#fff calc(100% / 73 * 25),#DD7308 0,#DD7308);
      border-radius: 100vmax;
      border: none;
      z-index: 2;
      width: calc(100% / 7 * (7 - 1) + 16px);
      &::-webkit-slider-thumb {
        -webkit-appearance: none;
        background-color: #3569c0;
        width: 16px;
        height: 16px;
        border-radius: 50%;
        box-shadow: none;
      }
      &::-moz-range-thumb {
        background: #3569c0;
        width: 16px;
        height: 16px;
        border-radius: 50%;
        box-shadow: none;
        border: none;
      }
      &:is(.future *) {
        background: linear-gradient(90deg,#fff,#fff calc(100% / 73 * 25),#DD7308 0,#DD7308);
        &::-webkit-slider-thumb {
          background-color: #DD7308;
        }
        &::-moz-range-thumb {
          background-color: #DD7308;
        }
      }
    }
    .list {
      display: flex;
      pointer-events: none;
    }
    .scale {
      position: relative;
      flex: 1;
      padding-top: 12px;
      text-align: center;
      font-size: 12px;
      font-weight: 600;
      line-height: 12px;
      color: #303030;
      white-space: pre-wrap;
      &[data-status="future"] {
        color: #DD7308;
        &::before {
          background-color: #DD7308;
        }
      }
      &::before {
        position: absolute;
        top: 0;
        left: 50%;
        transform: translateX(-50%);
        width: 1px;
        height: 10px;
        background-color: #8793ae;
        content: '';
      }
      &:nth-child(2n) {
        display: none;
      }
    }
  }
  @media (min-width: 830px) {
    .range {
      input {
        width: calc(100% / 13 * 12 + 16px);
      }
      .scale {
        &:nth-child(2n) {
          display: block;
        }
      }
    }
  }
}
</style>
